'use client';

import { Accordion, AccordionDetails, AccordionSummary, Button, Skeleton, Tooltip } from '@/shared/components';
import { useState, useMemo } from 'react';
import { PricingAvailabilityTable, ProductImage } from '@/shared/pageComponents';
import { CatalogProducts } from '@/types/search-result';
import Link from 'next/link';
import { NAMESPACE, useLang } from '@/hooks/use-lang';
import HoldCodeMessage from '@/shared/pageComponents/hold-code-message';
import { useQueryHoldCode } from '@/hooks/product-detail.hook';
import { useQueryCatalogNotes, useQueryChildCatalogs } from '@/hooks/search-result.hook';
import { AddCartFailMessage } from '@/shared/pageComponents/add-cart-fail-message';
import { useProductClickEventTracking, usePlacementAndMethod } from '@/shared/tracking/use-tracking-helpers';
import { useEventTracking } from '@/shared/tracking/use-tracking';
import { useCountry } from '@/hooks/use-country';
import { usePriceStore } from '@/store/price-store';
import { getProductPrice } from '@/shared/tracking/utils';
import { PriceErrorMessage } from '@/shared/pageComponents/price-error-message';
import { buildPdpLink } from '@/app/utils/build-pdp-link';
import { uniqueTitle } from '@/app/utils';

interface IProps {
  product: CatalogProducts;
  index: number;
}

export default function ProductCard({ product, index }: IProps) {
  const { t: commonT } = useLang(NAMESPACE.common);
  const { t } = useLang(NAMESPACE.search);
  const [expanded, setExpanded] = useState(false);
  const { isKR } = useCountry();
  const [addCartFailMessage, setAddCartFailMessage] = useState([]);
  const [priceFailSkus, setPriceFailSkus] = useState([]);
  const { data: holdCodeResponse } = useQueryHoldCode(product.childCatalogNumber, expanded);
  const { data: childCatalogs, isFetching: childCatalogLoading } = useQueryChildCatalogs(
    product.childCatalogNumber,
    expanded
  );

  const childCatalogNumbers = useMemo(() => {
    return childCatalogs?.map((item) => item.childCatalogNumber) || [];
  }, [childCatalogs]);

  const { data: childCatalogData, isLoading } = useQueryCatalogNotes(childCatalogNumbers, expanded);
  const { isCN } = useCountry();

  const handlePricingAvailability = () => {
    setExpanded(!expanded);
  };

  // tracking info
  const placement = usePlacementAndMethod();
  const { trackEvent } = useEventTracking();
  const { productPrice } = usePriceStore();
  const onCartAdd = async (products) => {
    const res = await window.miniCart?.addProductsToCart(products);
    setAddCartFailMessage(res?.data?.failWithMessage);
    // add to cart tracking
    trackEvent({
      key: 'cart',
      action: 'addToCart',
      payload: {
        products: products.map((item) => {
          const price = getProductPrice(productPrice[item.catalogNumber]) * item.quantity;
          return {
            name: uniqueTitle(product?.catalogName),
            familyId: product?.rootCatalogNumber,
            catalogId: item.catalogNumber,
            quantity: item.quantity,
            price: price,
            uom: item.unitSize,
            placement: placement.placement,
            method: placement.method,
          };
        }),
      },
    });
  };

  return (
    <div className="flex sm:flex-col w-full" style={{ borderBottom: '1px solid #E5E5E5' }}>
      <ProductImage src={product.imageUrl} width={114} height={114} className="ml-5 mt-5" />
      <Accordion expanded={expanded} showExpandIcon={false} className="border-none">
        <AccordionSummary className="p-2">
          <div className="flex flex-col ml-9 sm:m-0 cursor-auto">
            <div className="flex sm:flex-col">
              <div className="flex gap-x-4 mr-[60px]">
                <span className="font-normal">{t('catalog.number')}</span>
                <span>{product.rootCatalogNumber}</span>
              </div>
              <div className="flex gap-x-4">
                <span className="font-normal">{t('sub.branding')}</span>

                {product?.catalogBrand === undefined ? (
                  <Skeleton width="100px" rows={1} />
                ) : (
                  <span className="text-gray-900" dangerouslySetInnerHTML={{ __html: product?.catalogBrand }}></span>
                )}
              </div>
            </div>
            <PorductTitle product={product} index={index} />
            {product.synonym && (
              <div className="mb-2 flex items-center">
                <span className="font-bold flex-shrink-0">{t('synonym')}</span>
                {isCN && (
                  <Tooltip className="flex items-center" placement="top" maxWidth={278} title={t('synonym.tooltip')}>
                    <div className="icon-16-info-bold ml-1 cursor-pointer text-info" />
                  </Tooltip>
                )}
                <span className="text-[#81828C] ml-5">{product.synonym}</span>
              </div>
            )}
            <div className="flex sm:flex-col md:flex-col">
              <div className="grid grid-cols-3 md:grid-cols-2 sm:grid-cols-1 gap-x-6 gap-y-2 flex-1">
                {isKR && (
                  <LabelItem
                    label={commonT('cas')}
                    value={product?.cas}
                    tooltipVisible={false}
                    link={`/search/search-result?query=${product?.cas}`}
                  />
                )}
                {!isKR && <LabelItem label={commonT('cas')} value={product?.cas} />}
                <LabelItem label={commonT('formula')} value={product?.formula} />
                <LabelItem label={commonT('hazardClass')} value={product?.hazardClass} />
                <LabelItem label={commonT('einecs')} value={product?.einecs} />
                <LabelItem label={commonT('packagingGroup')} value={product?.packagingGroup} />
                <LabelItem label={commonT('mdl')} value={product?.mdl} />
              </div>
              <Button
                className="flex items-center justify-center min-w-fit ml-6 sm:m-0 md:m-0 sm:mt-4 md:mt-4"
                type="outline"
                endIconSize="8"
                size="small"
                endIconName={expanded ? 'caret-up' : 'caret-down'}
                onClick={handlePricingAvailability}
              >
                <span className="flex-1">{t('pricing.availability')}</span>
              </Button>
            </div>
            {expanded && (
              <div className="gap-y-2 my-2">
                <HoldCodeMessage holdCodes={holdCodeResponse?.data} />
                <AddCartFailMessage failList={addCartFailMessage} onClose={() => setAddCartFailMessage([])} />
                <PriceErrorMessage failSkus={priceFailSkus} onClose={() => setPriceFailSkus([])} />
              </div>
            )}
          </div>
        </AccordionSummary>
        <AccordionDetails className="flex">
          <div className="ml-5 sm:ml-0 md:ml-0 w-0 flex-1">
            <PricingAvailabilityTable
              getPriceFailSkus={(skus) => setPriceFailSkus(skus)}
              onCartAdd={onCartAdd}
              requestBulkText={t('request.bulk.quote')}
              childCatalogs={childCatalogs}
              childCatalogDetail={childCatalogData}
              noteLoading={isLoading}
              showLoading={childCatalogLoading}
              bulkQuoteLink={{
                pathname: '/custom-bulk',
                query: {
                  sku: product.rootCatalogNumber,
                  cas: product.cas,
                  title: product.catalogName,
                },
              }}
            />
          </div>
        </AccordionDetails>
      </Accordion>
    </div>
  );
}

function PorductTitle({ product, index }: Pick<IProps, 'product' | 'index'>) {
  const { isKR } = useCountry();
  const { t, lang } = useLang(NAMESPACE.search);
  const productClickEventTracking = useProductClickEventTracking();
  const enTitle = lang === 'en' ? product?.catalogName : product?.catalogTitle;
  const localTitle = lang === 'en' ? product?.catalogTitle : product?.catalogName;

  if (isKR) {
    return (
      <Link
        href={buildPdpLink(product.childCatalogNumber)}
        prefetch
        className="text-info text-2xl font-medium my-3 md:text-xl sm:text-lg"
        dangerouslySetInnerHTML={{ __html: uniqueTitle(product.catalogName) }}
        onClick={(e) => {
          productClickEventTracking(product, index);
        }}
      />
    );
  }

  const localTitleComponent = () => {
    if (enTitle === localTitle) return;
    return (
      <div className="flex items-center">
        <Link
          href={buildPdpLink(product.childCatalogNumber)}
          prefetch
          className="text-info text-xl font-medium md:text-xl sm:text-lg"
          dangerouslySetInnerHTML={{ __html: uniqueTitle(localTitle) }}
          onClick={(e) => {
            productClickEventTracking(product, index);
          }}
        />
        {enTitle && localTitle && (
          <Tooltip placement="top" maxWidth={278} title="产品描述请以英文为准，中文仅做参考。">
            <div className="icon-16-info-bold ml-1 cursor-pointer text-info" />
          </Tooltip>
        )}
      </div>
    );
  };

  return (
    <>
      {product?.catalogTitle === undefined ? (
        <Skeleton rows={2} width={['50%', '40%']} horizontal="ltr" className="flex flex-col my-4" height="56px" />
      ) : (
        <div className="flex flex-col my-1">
          <Link
            href={buildPdpLink(product.childCatalogNumber)}
            prefetch
            className="text-info text-xl font-medium md:text-xl sm:text-lg"
            dangerouslySetInnerHTML={{ __html: uniqueTitle(enTitle) }}
            onClick={(e) => {
              productClickEventTracking(product, index);
            }}
          />
          {localTitleComponent()}
        </div>
      )}
    </>
  );
}

interface ILabelItem {
  label: string;
  value: string;
  tooltipVisible?: boolean;
  link?: string;
}

function LabelItem({ label, value, tooltipVisible, link }: ILabelItem) {
  return (
    <>
      {value && (
        <div className="flex gap-x-4 whitespace-nowrap items-center">
          <span className="font-bold uppercase">{label}</span>
          {tooltipVisible !== false && (
            <Tooltip
              placement="top"
              title={<span dangerouslySetInnerHTML={{ __html: value }} />}
              className="overflow-hidden text-ellipsis"
            >
              <span dangerouslySetInnerHTML={{ __html: value }} />
            </Tooltip>
          )}
          {link && tooltipVisible === false && (
            <Link href={link} target="_blank" prefetch>
              {value}
            </Link>
          )}
          {!link && tooltipVisible === false && <span dangerouslySetInnerHTML={{ __html: value }} />}
        </div>
      )}
    </>
  );
}
